import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ParticularService from '../services/ParticularService';
import particularesApiService from '../services/ParticularesApiService';
import Alert from './common/Alert';
import SubscriptionBlockedModal from './common/SubscriptionBlockedModal';
import { AulaEmptyState, AulaOverview, AulaSection } from './common/AulaShell';
import StudentShell, { StudentModal } from './common/StudentShell';
import {
  CALENDARIO_SUBSCRIPTION_BLOCKED_MESSAGE,
  isParticularCalendarioAllowed,
  isParticularCalendarioMenuItem
} from '../utils/particularChatGPTAccess';
import './NewDashboard.css';

const getId = (item) => String(item?.id || item?.Id || '');
const getName = (item, fallback = 'Sin nombre') => item?.nombre || item?.Nombre || fallback;
const getDescripcion = (item) => item?.descripcion || item?.Descripcion || '';
const getList = (item, lowerKey, upperKey) => {
  const value = item?.[lowerKey] || item?.[upperKey] || [];
  return Array.isArray(value) ? value : [];
};
const getValue = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
  }
  return undefined;
};
const getEmail = (item) => item?.email || item?.Email || '';
const getFullName = (item, fallback = 'Sin nombre') => {
  const nombre = getValue(item, 'nombre', 'Nombre') || '';
  const apellido = getValue(item, 'apellido', 'Apellido') || '';
  const display = getValue(item, 'displayName', 'DisplayName') || '';
  return `${nombre} ${apellido}`.trim() || display || getEmail(item) || fallback;
};
const pad = (value) => String(value).padStart(2, '0');
const toDateKey = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};
const toTimeKey = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  return `${pad(value.getHours())}:${pad(value.getMinutes())}`;
};
const formatDateLabel = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  return value.toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: 'short' });
};
const formatDuration = (minutes) => {
  const total = Number(minutes) || 0;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours && rest) return `${hours} h ${rest} min`;
  if (hours) return `${hours} h`;
  return `${rest} min`;
};
const normalizeColor = (color) => {
  const value = String(color || '').trim();
  return /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(value) ? value : '#2563eb';
};
const normalizeCalendarSlot = (item) => {
  const profesor = getValue(item, 'profesor', 'Profesor') || {};
  return {
    id: getId(item),
    profesor,
    profesorNombre: getFullName(profesor, 'Profesor'),
    profesorColor: normalizeColor(getValue(profesor, 'colorCalendario', 'ColorCalendario')),
    fechaInicioLocal: getValue(item, 'fechaInicioLocal', 'FechaInicioLocal'),
    fechaFinLocal: getValue(item, 'fechaFinLocal', 'FechaFinLocal'),
    duracionMinutos: Number(getValue(item, 'duracionMinutos', 'DuracionMinutos')) || 60,
    estado: getValue(item, 'estado', 'Estado') || '',
    notas: getValue(item, 'notas', 'Notas') || '',
    notasAlumno: getValue(item, 'notasAlumno', 'NotasAlumno') || '',
    jitsiRoomName: getValue(item, 'jitsiRoomName', 'JitsiRoomName') || '',
    jitsiRoomUrl: getValue(item, 'jitsiRoomUrl', 'JitsiRoomUrl') || '',
    jitsiCreadaUtc: getValue(item, 'jitsiCreadaUtc', 'JitsiCreadaUtc') || '',
    asignada: Boolean(getValue(item, 'asignada', 'Asignada')),
    disponible: Boolean(getValue(item, 'disponible', 'Disponible')),
    cantidadAlumnos: Number(getValue(item, 'cantidadAlumnos', 'CantidadAlumnos')) || 0
  };
};

const getDayParts = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return { weekday: '', day: '', month: '' };
  return {
    weekday: value.toLocaleDateString('es-AR', { weekday: 'short' }).replace('.', ''),
    day: pad(value.getDate()),
    month: value.toLocaleDateString('es-AR', { month: 'short' }).replace('.', '')
  };
};
const groupSlotsByDay = (slots) => {
  const groups = new Map();
  [...slots]
    .sort((a, b) => new Date(a.fechaInicioLocal) - new Date(b.fechaInicioLocal))
    .forEach((slot) => {
      const key = toDateKey(slot.fechaInicioLocal);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(slot);
    });
  return [...groups.entries()].map(([key, items]) => ({ key, items }));
};

const ParticularAlumnoPanel = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState('panel');
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [contexto, setContexto] = useState(null);
  const [cursos, setCursos] = useState([]);
  const [calendarSlots, setCalendarSlots] = useState({ asignadas: [], disponibles: [] });
  const [reservationSlot, setReservationSlot] = useState(null);
  const [classDetailSlot, setClassDetailSlot] = useState(null);
  const [reservationNote, setReservationNote] = useState('');
  const [reservationSaving, setReservationSaving] = useState(false);
  const [subscriptionBlockedOpen, setSubscriptionBlockedOpen] = useState(false);

  const roleName = (user?.roleName || user?.nombreRol || user?.role || '').toLowerCase();
  const isAlumnoParticular = roleName.includes('alumnoparticular') || (roleName.includes('alumno') && roleName.includes('particular'));
  const activeParticular = ParticularService.getActiveParticular();
  const loadedContextParticular = contexto?.particular || contexto?.Particular || null;
  const contextParticular = loadedContextParticular || activeParticular;
  const calendarioPermissionReady = Boolean(loadedContextParticular);
  const canUseCalendarioFeature = isParticularCalendarioAllowed(contextParticular);
  const particularName = getName(contextParticular, 'Particular');
  const userName = user?.displayName || user?.name || 'Alumno';
  const firstName = String(user?.nombre || userName).trim().split(/\s+/)[0];

  const loadPanel = useCallback(async () => {
    setIsLoading(true);
    setAlert((prev) => ({ ...prev, show: false }));
    try {
      const [contextoRes, panelRes] = await Promise.all([
        particularesApiService.getContexto(),
        particularesApiService.getAlumnoPanel()
      ]);
      setContexto(contextoRes);
      setCursos(getList(panelRes, 'cursos', 'Cursos'));
    } catch (error) {
      setAlert({
        show: true,
        title: 'No pudimos cargar tus aulas',
        message: error.message || 'Intenta nuevamente.',
        type: 'error'
      });
      setCursos([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPanel();
  }, [loadPanel]);

  useEffect(() => {
    const requestedMenu = location.state?.activeMenu || location.state?.tab;
    if (requestedMenu === 'panel') {
      setActiveMenu('panel');
      return;
    }
    if (requestedMenu === 'calendario') {
      if (!calendarioPermissionReady) return;
      if (!canUseCalendarioFeature) {
        setActiveMenu('panel');
        setSubscriptionBlockedOpen(true);
        return;
      }
      setActiveMenu('calendario');
    }
  }, [calendarioPermissionReady, canUseCalendarioFeature, location.state]);

  // silent: carga de fondo para mostrar la próxima clase en "Mi espacio" sin avisos de error.
  const loadAlumnoCalendario = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setCalendarLoading(true);
      setAlert((prev) => ({ ...prev, show: false }));
    }
    try {
      const from = toDateKey(new Date());
      const to = toDateKey(new Date(Date.now() + 1000 * 60 * 60 * 24 * 90));
      const response = await particularesApiService.getAlumnoCalendario({ from, to });
      const asignadas = getList(response, 'asignadas', 'Asignadas').map(normalizeCalendarSlot);
      const disponibles = getList(response, 'disponibles', 'Disponibles').map(normalizeCalendarSlot);
      setCalendarSlots({ asignadas, disponibles });
    } catch (error) {
      if (silent) return;
      setAlert({
        show: true,
        title: 'No pudimos cargar el calendario',
        message: error.message || 'Intenta nuevamente.',
        type: 'error'
      });
      setCalendarSlots({ asignadas: [], disponibles: [] });
    } finally {
      if (!silent) setCalendarLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!calendarioPermissionReady) return;
    if (activeMenu !== 'calendario') {
      if (canUseCalendarioFeature) loadAlumnoCalendario({ silent: true });
      return;
    }
    if (!canUseCalendarioFeature) {
      setActiveMenu('panel');
      setSubscriptionBlockedOpen(true);
      return;
    }
    loadAlumnoCalendario();
  }, [activeMenu, calendarioPermissionReady, canUseCalendarioFeature, loadAlumnoCalendario]);

  const filteredCursos = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return cursos;

    return cursos
      .map((curso) => {
        const aulas = getList(curso, 'aulas', 'Aulas');
        const courseMatches = [
          getName(curso, ''),
          getDescripcion(curso)
        ].some((value) => String(value || '').toLowerCase().includes(term));

        if (courseMatches) return curso;

        const filteredAulas = aulas.filter((aula) => [
          getName(aula, ''),
          getDescripcion(aula),
          aula?.nicknameUrl || aula?.NicknameUrl || ''
        ].some((value) => String(value || '').toLowerCase().includes(term)));

        return filteredAulas.length > 0 ? { ...curso, aulas: filteredAulas, Aulas: filteredAulas } : null;
      })
      .filter(Boolean);
  }, [cursos, search]);

  const totalAulas = useMemo(() => cursos.reduce((acc, curso) => acc + getList(curso, 'aulas', 'Aulas').length, 0), [cursos]);
  const assignedGroups = useMemo(() => groupSlotsByDay(calendarSlots.asignadas), [calendarSlots.asignadas]);
  const availableGroups = useMemo(() => groupSlotsByDay(calendarSlots.disponibles), [calendarSlots.disponibles]);
  const nextClass = assignedGroups[0]?.items[0] || null;

  const openAula = (aula) => {
    const id = getId(aula);
    if (!id) return;
    navigate(`/particular/aula/${encodeURIComponent(id)}`);
  };

  const handleMenuNavigate = (item) => {
    if (!item) return;

    if (isParticularCalendarioMenuItem(item) && calendarioPermissionReady && !canUseCalendarioFeature) {
      setSubscriptionBlockedOpen(true);
      setActiveMenu('panel');
      return;
    }

    if (item.id === 'panel' || item.id === 'calendario') {
      setActiveMenu(item.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (item.path) {
      navigate(item.path, item.state ? { state: item.state } : undefined);
    }
  };

  const handleRefresh = () => {
    if (activeMenu === 'calendario') {
      if (calendarioPermissionReady && !canUseCalendarioFeature) {
        setSubscriptionBlockedOpen(true);
        setActiveMenu('panel');
        return;
      }
      loadAlumnoCalendario();
      return;
    }
    loadPanel();
  };

  const openReservationModal = (slot) => {
    setReservationSlot(slot);
    setReservationNote('');
  };

  const closeReservationModal = () => {
    if (reservationSaving) return;
    setReservationSlot(null);
    setReservationNote('');
  };

  const openClassDetailModal = (slot) => {
    setClassDetailSlot(slot);
  };

  const closeClassDetailModal = () => {
    setClassDetailSlot(null);
  };

  const openJitsiRoom = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleAssignedSlotKeyDown = (event, slot) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openClassDetailModal(slot);
    }
  };

  const confirmReservation = async (event) => {
    event.preventDefault();
    if (!reservationSlot?.id) return;

    setReservationSaving(true);
    setAlert((prev) => ({ ...prev, show: false }));
    try {
      await particularesApiService.reservarAlumnoDisponibilidad(reservationSlot.id, {
        notasAlumno: reservationNote
      });
      setReservationSlot(null);
      setReservationNote('');
      setAlert({
        show: true,
        title: 'Calendario',
        message: 'La clase quedó reservada correctamente.',
        type: 'success'
      });
      await loadAlumnoCalendario();
    } catch (error) {
      setAlert({
        show: true,
        title: 'No pudimos reservar la clase',
        message: error.message || 'Intenta nuevamente.',
        type: 'error'
      });
    } finally {
      setReservationSaving(false);
    }
  };

  const formatSlotRange = (slot) => (
    `${formatDateLabel(slot.fechaInicioLocal)} · ${toTimeKey(slot.fechaInicioLocal)} - ${toTimeKey(slot.fechaFinLocal)}`
  );

  const renderCalendarSlot = (slot, mode) => (
    <article
      className={`sp-slot ${mode}`}
      key={slot.id}
      style={{ '--profesor-color': slot.profesorColor }}
      role={mode === 'assigned' ? 'button' : undefined}
      tabIndex={mode === 'assigned' ? 0 : undefined}
      onClick={mode === 'assigned' ? () => openClassDetailModal(slot) : undefined}
      onKeyDown={mode === 'assigned' ? (event) => handleAssignedSlotKeyDown(event, slot) : undefined}
    >
      <div className="sp-slot-time">
        <strong>{toTimeKey(slot.fechaInicioLocal)}</strong>
        <span>{toTimeKey(slot.fechaFinLocal)}</span>
      </div>
      <div className="sp-slot-main">
        <strong>{slot.profesorNombre}</strong>
        <span className="sp-slot-meta">
          <span><i className="far fa-clock" aria-hidden="true" /> {formatDuration(slot.duracionMinutos)}</span>
          {mode === 'assigned' && slot.jitsiRoomUrl && (
            <span className="ready"><i className="fas fa-video" aria-hidden="true" /> Sala lista</span>
          )}
        </span>
        {slot.notas && <p>{slot.notas}</p>}
        {slot.notasAlumno && <p className="sp-slot-note">Tu nota: {slot.notasAlumno}</p>}
      </div>
      {mode === 'available' ? (
        <button type="button" className="btn-primary" onClick={() => openReservationModal(slot)}>
          Reservar
        </button>
      ) : (
        <i className="fas fa-chevron-right sp-slot-go" aria-hidden="true" />
      )}
    </article>
  );

  const renderSlotGroups = (groups, mode) => groups.map((group) => {
    const parts = getDayParts(group.items[0].fechaInicioLocal);
    return (
      <div className="sp-day" key={group.key}>
        <div className="sp-day-date">
          <small>{parts.weekday}</small>
          <strong>{parts.day}</strong>
          <span>{parts.month}</span>
        </div>
        <div className="sp-day-slots">
          {group.items.map((slot) => renderCalendarSlot(slot, mode))}
        </div>
      </div>
    );
  });

  if (!isAlumnoParticular) {
    return <Navigate to="/particular" replace />;
  }

  const refreshing = activeMenu === 'calendario' ? calendarLoading : isLoading;
  const shortcuts = [
    {
      key: 'calendario',
      id: 'calendario',
      label: 'Calendario',
      icon: 'fa-calendar-alt',
      requiresCalendarAccess: true,
      description: nextClass
        ? `Próxima clase: ${formatSlotRange(nextClass)} con ${nextClass.profesorNombre}.`
        : 'Mirá tus clases y reservá horarios con tus profesores.',
      count: canUseCalendarioFeature ? calendarSlots.asignadas.length : undefined
    },
    {
      key: 'mensajes',
      id: 'mensajes',
      label: 'Mensajes',
      icon: 'fa-envelope',
      path: '/mensajes',
      state: { scope: 'particulares' },
      description: 'Escribile a tus profesores y revisá tu bandeja.'
    }
  ];

  return (
    <StudentShell
      activeId={activeMenu}
      onNavigate={handleMenuNavigate}
      user={user}
      onLogout={logout}
      particularName={particularName}
      headerActions={(
        <button type="button" className="btn-secondary" onClick={handleRefresh} disabled={refreshing} title="Actualizar">
          <i className={`fas ${refreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} aria-hidden="true" />
          <span className="sp-hide-mobile">Actualizar</span>
        </button>
      )}
    >
      {alert.show && (
        <Alert
          title={alert.title}
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
        />
      )}

      {activeMenu === 'calendario' ? (
        <div className="sp-calendar-grid">
          <AulaSection
            active
            icon="fa-calendar-check"
            title="Mis clases"
            subtitle={calendarLoading ? 'Cargando...' : `${calendarSlots.asignadas.length} clases en los próximos 90 días`}
          >
            {assignedGroups.length === 0 ? (
              <AulaEmptyState icon="fa-calendar-check">
                {calendarLoading ? 'Cargando tus clases...' : 'No tenés clases asignadas en los próximos días.'}
              </AulaEmptyState>
            ) : renderSlotGroups(assignedGroups, 'assigned')}
          </AulaSection>

          <AulaSection
            active
            icon="fa-calendar-plus"
            title="Horarios disponibles"
            subtitle={calendarLoading ? 'Cargando...' : `${calendarSlots.disponibles.length} horarios para reservar`}
          >
            {availableGroups.length === 0 ? (
              <AulaEmptyState icon="fa-calendar-plus">
                {calendarLoading ? 'Cargando horarios...' : 'No hay horarios libres para reservar por ahora.'}
              </AulaEmptyState>
            ) : renderSlotGroups(availableGroups, 'available')}
          </AulaSection>
        </div>
      ) : (
        <>
          <div className="aula-home">
            <section className="aula-hero">
              <span className="aula-hero-badge">
                <i className="fas fa-user-graduate" aria-hidden="true" />
                Alumno particular
              </span>
              <h1>Hola, {firstName}</h1>
              <p className="aula-hero-desc">
                Este es tu espacio en {particularName}: entrá a tus aulas, organizá tus clases y seguí tus mensajes.
              </p>
              <dl className="aula-hero-meta">
                <div>
                  <dt>Cursos</dt>
                  <dd>{cursos.length}</dd>
                </div>
                <div>
                  <dt>Aulas</dt>
                  <dd>{totalAulas}</dd>
                </div>
                {nextClass && (
                  <div>
                    <dt>Próxima clase</dt>
                    <dd>{formatSlotRange(nextClass)}</dd>
                  </div>
                )}
              </dl>
            </section>
            <AulaOverview
              items={shortcuts}
              onSelect={(key) => handleMenuNavigate(shortcuts.find((item) => item.key === key))}
            />
          </div>

          <AulaSection
            active
            icon="fa-layer-group"
            title="Mis cursos y aulas"
            subtitle={`${cursos.length} ${cursos.length === 1 ? 'curso' : 'cursos'} · ${totalAulas} ${totalAulas === 1 ? 'aula' : 'aulas'}`}
            actions={(
              <div className="sp-search">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Buscar curso o aula"
                  aria-label="Buscar curso o aula"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            )}
          >
            {isLoading ? (
              <div className="aula-loading soft">
                <div className="spinner"></div>
                <p>Cargando aulas...</p>
              </div>
            ) : filteredCursos.length === 0 ? (
              <AulaEmptyState icon="fa-layer-group">
                {search.trim() ? 'No encontramos cursos ni aulas con esa búsqueda.' : 'Todavía no tenés cursos asignados.'}
              </AulaEmptyState>
            ) : (
              <div className="sp-course-grid">
                {filteredCursos.map((curso) => {
                  const aulas = getList(curso, 'aulas', 'Aulas');
                  return (
                    <article className="sp-course-card" key={getId(curso)}>
                      <header className="sp-course-head">
                        <span className="sp-course-icon">
                          <i className="fas fa-book-open" aria-hidden="true" />
                        </span>
                        <div>
                          <h3>{getName(curso)}</h3>
                          {getDescripcion(curso) && <p>{getDescripcion(curso)}</p>}
                        </div>
                        <span className="sp-chip">{aulas.length} {aulas.length === 1 ? 'aula' : 'aulas'}</span>
                      </header>

                      {aulas.length === 0 ? (
                        <p className="sp-course-empty">Este curso todavía no tiene aulas.</p>
                      ) : (
                        <ul className="sp-aula-list">
                          {aulas.map((aula) => {
                            const detalle = getDescripcion(aula) || aula?.nicknameUrl || aula?.NicknameUrl || '';
                            return (
                              <li key={getId(aula)}>
                                <button type="button" className="sp-aula-row" onClick={() => openAula(aula)}>
                                  <span className="sp-aula-icon">
                                    <i className="fas fa-chalkboard" aria-hidden="true" />
                                  </span>
                                  <span className="sp-aula-copy">
                                    <strong>{getName(aula)}</strong>
                                    {detalle && <small>{detalle}</small>}
                                  </span>
                                  <span className="sp-aula-go">
                                    <span>Ingresar</span>
                                    <i className="fas fa-arrow-right" aria-hidden="true" />
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </AulaSection>
        </>
      )}

      {reservationSlot && (
        <StudentModal
          eyebrow="Reservar clase"
          title={reservationSlot.profesorNombre}
          subtitle={`${formatSlotRange(reservationSlot)} · ${formatDuration(reservationSlot.duracionMinutos)}`}
          onClose={closeReservationModal}
          onSubmit={confirmReservation}
          footer={(
            <>
              <button type="button" className="btn-secondary" onClick={closeReservationModal} disabled={reservationSaving}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={reservationSaving}>
                <i className={`fas ${reservationSaving ? 'fa-spinner fa-spin' : 'fa-check'}`} aria-hidden="true" />
                Confirmar reserva
              </button>
            </>
          )}
        >
          <label className="sp-field">
            Nota para el profesor
            <textarea
              value={reservationNote}
              onChange={(event) => setReservationNote(event.target.value)}
              maxLength={500}
              disabled={reservationSaving}
              placeholder="Contale qué querés ver en la clase (opcional)"
            />
            <small>{reservationNote.length}/500</small>
          </label>
        </StudentModal>
      )}

      {classDetailSlot && (
        <StudentModal
          eyebrow="Clase asignada"
          title={classDetailSlot.profesorNombre}
          subtitle={formatSlotRange(classDetailSlot)}
          onClose={closeClassDetailModal}
          footer={(
            <>
              <button type="button" className="btn-secondary" onClick={closeClassDetailModal}>
                Cerrar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => openJitsiRoom(classDetailSlot.jitsiRoomUrl)}
                disabled={!classDetailSlot.jitsiRoomUrl}
              >
                <i className="fas fa-video" aria-hidden="true" />
                Ingresar a la clase
              </button>
            </>
          )}
        >
          <div className="sp-facts">
            <div>
              <span>Duración</span>
              <strong>{formatDuration(classDetailSlot.duracionMinutos)}</strong>
            </div>
            <div>
              <span>Participantes</span>
              <strong>{classDetailSlot.cantidadAlumnos || 1}</strong>
            </div>
            <div>
              <span>Videollamada</span>
              <strong>{classDetailSlot.jitsiRoomUrl ? 'Sala lista' : 'Pendiente'}</strong>
            </div>
          </div>

          {classDetailSlot.notas && (
            <section className="sp-block">
              <h4>Notas del profesor</h4>
              <p>{classDetailSlot.notas}</p>
            </section>
          )}

          {classDetailSlot.notasAlumno && (
            <section className="sp-block">
              <h4>Tu nota</h4>
              <p>{classDetailSlot.notasAlumno}</p>
            </section>
          )}

          {!classDetailSlot.jitsiRoomUrl && (
            <section className="sp-block">
              <p>La sala todavía no está generada. Vas a poder ingresar cuando el profesor la habilite.</p>
            </section>
          )}
        </StudentModal>
      )}

      <SubscriptionBlockedModal
        open={subscriptionBlockedOpen}
        message={CALENDARIO_SUBSCRIPTION_BLOCKED_MESSAGE}
        onClose={() => setSubscriptionBlockedOpen(false)}
      />
    </StudentShell>
  );
};

export default ParticularAlumnoPanel;
